import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";

import * as MatStyled from "@mui/material/styles";
import {
  Grid,
  FormControl,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";

import font from "src/styles/font";
import { Black, DarkGrey, Primary, White01 } from "src/styles/color";
import { useAuth } from "src/hooks/useAuth";

import Button from "src/components/core/Button";
import LabelSection from "src/components/core/LabelSection";
import Loading from "src/components/ui/Loading";
import { convertIntToCurrency, convertPrice } from "src/common";
import { findOnePromoCode } from "src/api/promoCode";

const Container = styled.div`
  width: 100%;
  max-width: 1240px;
  position: relative;
  margin: 0 auto;
  padding: 32px;
  padding-bottom: 100px;
`;

const BoxWrapper = styled.div`
  width: 100%;
  height: auto;
  padding: 32px;
  border-radius: 32px;
  background-color: #ffffff;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  // display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 32px;
`;

const SpanLabel = styled.div`
  font-size: ${font.size.lg};
  color: var(--secondary-color);
  font-weight: 400;
  margin-bottom: 24px;

  span {
    font-size: ${font.size.lg};
    font-weight: 400;
    color: var(--primary-color);
  }
`;

const FieldValue = styled.div`
  width: 100%;
  padding: 24px 40px;
  background: ${White01};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  span {
    font-size: ${font.size.md};
    font-weight: 400;
    color: ${DarkGrey};
  }
`;

const PreviewDesc = styled.div`
  ul {
    margin: 0;
    padding-left: 1em;
  }
`;

const ImageNotFound = styled.div`
  width: 120px;
  height: 120px;
  background-image: url("https://xsurface-images.s3.ap-southeast-1.amazonaws.com/not+found.svg");
  background-repeat: no-repeat;
  background-size: 100%;
`;

const ImageNoData = styled.image`
  width: 120px;
  height: 120px;
  background-image: url("https://xsurface-images.s3.ap-southeast-1.amazonaws.com/no+data.svg");
  background-repeat: no-repeat;
  background-size: 100%;
`;

const TextNoData = styled.pre`
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
`;

const ImageEmpty = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: #f2f1f3;
  border-radius: 16px;
  object-fit: cover;
  object-position: center;
  cursor: pointer;
  vertical-align: initial;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }

  &.active {
    border: 2px solid #707070;
  }
`;

const ImgDisplay = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  object-position: center;
  border-radius: 16px;
  flex-shrink: 0;
`;

const ButtonGroup = styled(Stack)`
  /* width: 100%;
  max-width: 1240px; */

  width: 100%;
  height: 100px;
  background-color: var(--white-color);
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 15;
  /* margin-top: 24px; */
  padding: 24px 40px;
  border-radius: 24px 24px 0 0;
  box-shadow: 0px -2px 10px 0px rgba(0, 0, 0, 0.1);

  .MuiButton-root {
    padding: 12px 24px;
    font-size: ${font.size.sm};
  }
`;

const WrapperDataGrid = styled.div`
  height: 600px;
  width: 100%;
  .MuiDataGrid-root {
    border: 1px solid #ffffff;
  }
  .MuiDataGrid-columnHeaders {
    background-color: #f5f5f5;
  }
  .MuiDataGrid-root .MuiDataGrid-columnSeparator--sideRight {
    color: #f5f5f5;
  }
  .MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within {
    // outline: solid #f5f5f5 1px;
    outline: none;
  }
  .MuiDataGrid-root .MuiDataGrid-cell:focus {
    outline: none;
    border-bottom: solid #f5f5f5 1px 0 1px 0;
  }
  .MuiDataGrid-root .MuiDataGrid-row.Mui-selected {
    background-color: #ffffff;
  }
  .MuiCheckbox-root.Mui-checked {
    color: var(--primary-color);
  }
`;

const drawerWidth = 260;

const Main = MatStyled.styled("main", {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  position: "relative",
  flexGrow: 1,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginTop: "75px",
  width: "100%",
  height: "calc(100vh - 75px)",
  overflow: "hidden",
  overflowY: "scroll",
  scrollBehavior: "smooth",
  background: "#fff",
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
    position: "relative",
  }),
}));

function NoRowsOverlay() {
  return (
    <Stack height="100%" alignItems="center" justifyContent="center">
      <ImageNoData></ImageNoData>
      <TextNoData>No Data</TextNoData>
    </Stack>
  );
}

function NoResultsOverlay() {
  return (
    <Stack height="100%" alignItems="center" justifyContent="center">
      <ImageNotFound />
      <TextNoData>Not Found</TextNoData>
    </Stack>
  );
}

const PreviewPromoCode = ({ open }) => {
  const { codeId } = useParams();
  const navigate = useNavigate();
  const { checkAllowPermission } = useAuth();

  const [isLoading, setIsLoading] = useState(false);

  const defaultValues = {
    _id: null,
    promo_name: "",
    promo_code: "",
    promo_type: "product", // "product", "bill", "delivery"
    promo_condition: {
      promo_condition_user: "new", // "all", "new", "repeat"
      promo_condition_repeat: true,
    },
    promo_product_group: "ready", //"ready", "pre-order", "all"
    promo_pay_type: "promptpay", // "promptpay", "credit-card", "installment", "all"
    promo_discount: {
      promo_discount_type: "", // "percent", "amount"
      promo_discount_value: null,
      promo_discount_percent: null,
      promo_discount_minimum_spend: null,
      promo_discount_maximum: null,
    },
    promo_responsible: {
      promo_responsible_by: "xsurface", // "xsurface", "seller", "split"
      promo_xsurface_percent: null,
      promo_seller_percent: null,
    },
    promo_start_time: null,
    promo_expire_time: null,
    amount: null,
    used_amount: 0,
    promo_title: "",
    promo_description: "",
    product_choice_promocodes: [],
    note: "",
  };
  const [formData, setFormData] = useState(defaultValues);

  const fetchCodeData = async () => {
    setIsLoading(true);
    try {
      const data = await findOnePromoCode(codeId);

      if (!!data) {
        setFormData({
          ...data,
        });
      }
    } catch (err) {
      console.error("err", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (codeId === undefined) return;

    fetchCodeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeId]);

  const columns = [
    {
      field: "product_choice_promocode",
      headerName: "ชื่อสินค้า/รหัส",
      flex: 4,
      sortable: false,
      align: "left",
      userRoles: [1],
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              py: "10px",
            }}
          >
            {!!params?.row?.group_image?.product_display_image === false ? (
              <ImageEmpty>
                <InsertPhotoIcon
                  sx={{
                    color: "rgba(0, 0, 0, 0.54)",
                    cursor: "pointer",
                  }}
                />
              </ImageEmpty>
            ) : (
              <ImgDisplay
                src={
                  params?.row?.group_image?.product_display_image
                  // ? params?.row?.group_image?.product_display_image.replace(
                  //     ".com",
                  //     ".com/300x300"
                  //   )
                  // : ""
                }
                alt=""
              />
            )}
            <Stack
              gap={1}
              sx={{
                ml: 2,
                "& .MuiTypography-root": {
                  fontSize: font.size.sm,
                },
                "& span.MuiTypography-root": {
                  color: DarkGrey,
                },
              }}
            >
              <Typography component="p">
                {params?.row?.product?.item_name}
              </Typography>
              <Typography component="span">
                {params?.row?.product?.item_code}
              </Typography>
              {!!params?.row?.name && (
                <Typography component="span">{params?.row?.name}</Typography>
              )}
            </Stack>
          </Box>
        );
      },
    },
    {
      field: "company_name",
      headerName: "แบรนด์",
      flex: 2,
      sortable: false,
      headerAlign: "left",
      align: "left",
      userRoles: [1],
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            component="p"
            sx={{
              "&.MuiTypography-root": {
                fontSize: font.size.sm,
              },
            }}
          >
            {params?.row?.product?.brand?.name}
          </Typography>
        </Box>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Updated Date",
      flex: 3,
      sortable: true,
      headerAlign: "center",
      align: "center",
      userRoles: [1],
      renderCell: (params) =>
        dayjs(params?.row?.product_choice_deal?.updatedAt).format("DD-MM-YYYY"),
    },
    {
      field: "price",
      headerName: "Price",
      flex: 2,
      sortable: false,
      headerAlign: "center",
      align: "center",
      userRoles: [1],
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            component="p"
            sx={{
              "&.MuiTypography-root": {
                fontSize: font.size.sm,
              },
            }}
          >
            {`${convertPrice(
              params?.row?.group_price?.is_start_price === true
                ? params?.row?.group_price?.start_price
                : params?.row?.group_price.special_price > 0 &&
                    params?.row?.group_price.special_price !== null &&
                    params?.row?.group_price.special_price !== undefined
                  ? params?.row?.group_price.special_price
                  : params?.row?.group_price?.price,
            )}`}
          </Typography>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 2,
      sortable: false,
      headerAlign: "center",
      align: "center",
      userRoles: [1],
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            component="p"
            sx={{
              "&.MuiTypography-root": {
                fontSize: font.size.sm,
              },
            }}
          >
            {params?.row?.product?.status}
          </Typography>
        </Box>
      ),
    },
    // {
    //   field: "actions",
    //   headerName: "Del.",
    //   flex: 1,
    //   sortable: false,
    //   headerAlign: "center",
    //   align: "center",
    //   userRoles: [1],
    //   renderCell: (params) => (
    //     <IconButton
    //       sx={{
    //         color: Black,
    //         backgroundColor: hexToRgba(White01, 0.4),
    //       }}
    //       onClick={() => {
    //         openDialog({
    //           type: "confirm",
    //           title: "คุณต้องการลบใช่หรือไม่?",
    //           onConfirm: () => {
    //             let index = params.api.getRowIndex(params.row._id);
    //             removeElement(index);
    //             closeDialog();
    //           },
    //           maxWidth: 342,
    //         });
    //       }}
    //     >
    //       <DeleteOutlineRoundedIcon />
    //     </IconButton>
    //   ),
    // },
  ];

  return (
    <>
      <Main open={open}>
        <Container>
          <BoxWrapper>
            <LabelSection
              title="ชื่อ/ประเภทโค้ดส่วนลด"
              style={{ margin: 0 }}
              headerStyle={{ marginBottom: 24 }}
              titleStyle={{ fontSize: font.size.lg }}
            >
              <Grid container spacing={3} rowSpacing={3} columnSpacing={5}>
                <Grid item xs={12} md={6}>
                  <SpanLabel>ชื่อโค้ดส่วนลด</SpanLabel>
                  <FieldValue>
                    <Typography variant="body1">
                      {formData?.promo_name}
                    </Typography>
                  </FieldValue>
                </Grid>

                <Grid item xs={12} md={6}>
                  <SpanLabel>รหัสโค้ดส่วนลด</SpanLabel>
                  <FieldValue>
                    <Typography variant="body1">
                      {formData?.promo_code}
                    </Typography>
                  </FieldValue>
                </Grid>

                {/* // ! hold */}
                {/* <Grid item xs={12}>
                  <FormControl sx={{ width: "100%" }}>
                    <FormLabel
                      id="promo-code-type-radio-group-label"
                      sx={{
                        fontSize: font.size.lg,
                        color: "var(--secondary-color)",
                        mb: 2,
                        span: {
                          color: "var(--primary-color)",
                        },
                        "&.Mui-focused": {
                          color: "var(--secondary-color)",
                        },
                      }}
                    >
                      ประเภทโค้ดส่วนลด
                    </FormLabel>

                    <RadioGroup
                      row
                      aria-labelledby="promo-code-type-radio-group-label"
                      name="promo-code-type-radio-group"
                      sx={{
                        ml: -5,
                        ".MuiFormControlLabel-root": {
                          pl: 5,
                        },
                      }}
                      value={formData?.promo_type}
                    >
                      <FormControlLabel
                        value="product"
                        disabled
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                          ".MuiFormControlLabel-label.Mui-disabled": {
                            color: Black,
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label={"ส่วนลดสินค้า"}
                      />

                      <FormControlLabel
                        value="bill"
                        disabled
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                          ".MuiFormControlLabel-label.Mui-disabled": {
                            color: Black,
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label={"ส่วนลดท้ายบิล"}
                      />

                      <FormControlLabel
                        value="delivery"
                        disabled
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                          ".MuiFormControlLabel-label.Mui-disabled": {
                            color: Black,
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label={"ส่วนลดค่าขนส่ง"}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid> */}
                {/* // !! */}

                <Grid item xs={12}>
                  <FormControl sx={{ width: "100%" }}>
                    <FormLabel
                      id="discount-type-radio-group-label"
                      sx={{
                        fontSize: font.size.lg,
                        color: "var(--secondary-color)",
                        mb: 2,
                        span: {
                          color: "var(--primary-color)",
                        },
                        "&.Mui-focused": {
                          color: "var(--secondary-color)",
                        },
                      }}
                    >
                      การใช้ส่วนลด
                    </FormLabel>

                    <RadioGroup
                      row
                      aria-labelledby="promo-condition-repeat-radio-group-label"
                      name="promo-condition-repeat-radio-group"
                      sx={{
                        ml: -5,
                        ".MuiFormControlLabel-root": {
                          pl: 5,
                        },
                      }}
                      value={formData?.promo_condition?.promo_condition_repeat}
                    >
                      <FormControlLabel
                        value={true}
                        disabled
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                          ".MuiFormControlLabel-label.Mui-disabled": {
                            color: Black,
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="สามารถใช้ซ้ำได้"
                      />

                      <FormControlLabel
                        value={false}
                        disabled
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                          ".MuiFormControlLabel-label.Mui-disabled": {
                            color: Black,
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ไม่สามารถใช้ซ้ำได้"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </LabelSection>
          </BoxWrapper>

          <BoxWrapper>
            <LabelSection
              title="เงื่อนไขโค้ดส่วนลด"
              style={{ margin: 0 }}
              headerStyle={{ marginBottom: 24 }}
              titleStyle={{ fontSize: font.size.lg }}
            >
              <Grid container spacing={3} rowSpacing={3} columnSpacing={5}>
                <Grid item xs={12}>
                  <SpanLabel style={{ margin: 0 }}>
                    เงื่อนไขการใช้ลดส่วนสินค้า/ท้ายบิล
                  </SpanLabel>
                </Grid>

                <Grid item xs={12}>
                  <FormControl sx={{ width: "100%" }}>
                    <FormLabel
                      id="promo-condition-radio-group-label"
                      sx={{
                        fontSize: font.size.md,
                        color: "var(--secondary-color)",
                        mb: 2,
                        span: {
                          color: "var(--primary-color)",
                        },
                        "&.Mui-focused": {
                          color: "var(--secondary-color)",
                        },
                      }}
                    >
                      กลุ่มลูกค้า
                    </FormLabel>

                    <RadioGroup
                      row
                      aria-labelledby="promo-condition-radio-group-label"
                      name="promo-condition-radio-group"
                      sx={{
                        ml: -5,
                        ".MuiFormControlLabel-root": {
                          m: 0,
                          flex: "0 0 50%",
                          width: "50%",
                          pl: 5,
                        },
                      }}
                      value={formData?.promo_condition?.promo_condition_user}
                    >
                      <FormControlLabel
                        value="new"
                        disabled
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                          ".MuiFormControlLabel-label.Mui-disabled": {
                            color: Black,
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ลูกค้าใหม่"
                      />

                      <FormControlLabel
                        value="repeat"
                        disabled
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                          ".MuiFormControlLabel-label.Mui-disabled": {
                            color: Black,
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ลูกค้าซื้อซ้ำ"
                      />

                      <FormControlLabel
                        value="all"
                        disabled
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                          ".MuiFormControlLabel-label.Mui-disabled": {
                            color: Black,
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ลูกค้าทุกคน"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl sx={{ width: "100%" }}>
                    <FormLabel
                      id="promo-product-group-radio-group-label"
                      sx={{
                        fontSize: font.size.md,
                        color: "var(--secondary-color)",
                        mb: 2,
                        span: {
                          color: "var(--primary-color)",
                        },
                        "&.Mui-focused": {
                          color: "var(--secondary-color)",
                        },
                      }}
                    >
                      กลุ่มสินค้า
                    </FormLabel>

                    <RadioGroup
                      row
                      aria-labelledby="promo-product-group-radio-group-label"
                      name="promo-product-group-radio-group"
                      sx={{
                        ml: -5,
                        ".MuiFormControlLabel-root": {
                          m: 0,
                          flex: "0 0 50%",
                          width: "50%",
                          pl: 5,
                        },
                      }}
                      value={formData?.promo_product_group}
                    >
                      <FormControlLabel
                        value="ready"
                        disabled
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                          ".MuiFormControlLabel-label.Mui-disabled": {
                            color: Black,
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="สินค้าพร้อมส่ง"
                      />

                      <FormControlLabel
                        value="pre-order"
                        disabled
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                          ".MuiFormControlLabel-label.Mui-disabled": {
                            color: Black,
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="สินค้าพรีออเดอร์"
                      />

                      <FormControlLabel
                        value="all"
                        disabled
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                          ".MuiFormControlLabel-label.Mui-disabled": {
                            color: Black,
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="สินค้าทุกกลุ่ม"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl sx={{ width: "100%" }}>
                    <FormLabel
                      id="promo-pay-type-radio-group-label"
                      sx={{
                        fontSize: font.size.md,
                        color: "var(--secondary-color)",
                        mb: 2,
                        span: {
                          color: "var(--primary-color)",
                        },
                        "&.Mui-focused": {
                          color: "var(--secondary-color)",
                        },
                      }}
                    >
                      วิธีการชำระเงิน
                    </FormLabel>

                    <RadioGroup
                      row
                      aria-labelledby="promo-product-group-radio-group-label"
                      name="promo-product-group-radio-group"
                      sx={{
                        ml: -5,
                        ".MuiFormControlLabel-root": {
                          m: 0,
                          flex: "0 0 50%",
                          width: "50%",
                          pl: 5,
                        },
                      }}
                      value={formData?.promo_pay_type}
                    >
                      <FormControlLabel
                        value="promptpay"
                        disabled
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                          ".MuiFormControlLabel-label.Mui-disabled": {
                            color: Black,
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ชำระผ่านพร้อมเพย์/โอนผ่านบัญชีธนาคาร"
                      />

                      <FormControlLabel
                        value="credit-card"
                        disabled
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                          ".MuiFormControlLabel-label.Mui-disabled": {
                            color: Black,
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ชำระผ่านบัตรเครดิต"
                      />

                      {/* <FormControlLabel
                        value="installment"
                        disabled
                        sx={
                        {
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                          ".MuiFormControlLabel-label.Mui-disabled": {
                            color: Black,
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ผ่อนชำระ"
                      /> */}

                      <FormControlLabel
                        value="all"
                        disabled
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                          ".MuiFormControlLabel-label.Mui-disabled": {
                            color: Black,
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ทุกการชำระเงิน"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </LabelSection>
          </BoxWrapper>

          <BoxWrapper>
            <LabelSection
              title="รายละเอียดโค้ดส่วนลด"
              style={{ margin: 0 }}
              headerStyle={{ marginBottom: 24 }}
              titleStyle={{ fontSize: font.size.lg }}
            >
              <Grid container spacing={3} rowSpacing={3} columnSpacing={5}>
                <Grid item xs={12}>
                  <FormControl sx={{ width: "100%" }}>
                    <FormLabel
                      id="discount-type-radio-group-label"
                      sx={{
                        fontSize: font.size.lg,
                        color: "var(--secondary-color)",
                        mb: 2,
                        span: {
                          color: "var(--primary-color)",
                        },
                        "&.Mui-focused": {
                          color: "var(--secondary-color)",
                        },
                      }}
                    >
                      ประเภทของส่วนลด
                    </FormLabel>

                    <RadioGroup
                      row
                      aria-labelledby="discount-type-radio-group-label"
                      name="discount-type-radio-group"
                      sx={{
                        ml: -5,
                        // gap: 1,
                        ".MuiFormControlLabel-root": {
                          pl: 5,
                          zIndex: 2,
                        },
                      }}
                      value={formData?.promo_discount?.promo_discount_type}
                      defaultValue={
                        formData?.promo_discount?.promo_discount_type
                      }
                    >
                      <FormControlLabel
                        value={formData?.promo_discount?.promo_discount_type}
                        disabled
                        sx={{
                          flex: "1 1 100%",
                          width: "100%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                          ".MuiFormControlLabel-label.Mui-disabled": {
                            color: Black,
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label={
                          formData?.promo_discount?.promo_discount_type ===
                          "amount"
                            ? "ลดเป็นจำนวนเงิน / บาท"
                            : "ลดเป็นเปอร์เซ็น %"
                        }
                      />

                      <Box
                        sx={{
                          pl: 6,
                          pt: 2,
                          width: "100%",
                        }}
                      >
                        <FieldValue>
                          <Typography variant="body1">
                            <span>ส่วนลด : </span>
                            {formData?.promo_discount?.promo_discount_type ===
                            "amount"
                              ? `${convertIntToCurrency(
                                  formData?.promo_discount
                                    ?.promo_discount_value,
                                  false,
                                )} บาท`
                              : `${formData?.promo_discount?.promo_discount_percent}%`}
                          </Typography>

                          <Typography variant="body1">
                            <span>ขั้นต่ำในการสั่งซื้อ : </span>
                            {convertIntToCurrency(
                              formData?.promo_discount
                                ?.promo_discount_minimum_spend || 0,
                              false,
                            )}{" "}
                            บาท
                          </Typography>

                          {formData?.promo_discount?.promo_discount_type ===
                            "percent" && (
                            <Typography variant="body1">
                              <span>มูลค่าส่วนลดสูงสุด : </span>
                              {convertIntToCurrency(
                                formData?.promo_discount
                                  ?.promo_discount_maximum || 0,
                                false,
                              )}{" "}
                              บาท
                            </Typography>
                          )}
                        </FieldValue>
                      </Box>
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <SpanLabel>ระยะเวลาของส่วนลด</SpanLabel>

                  <FieldValue>
                    <Typography variant="body1">
                      {`${dayjs(formData?.promo_start_time).format(
                        "DD/MM/YYYY",
                      )} - ${dayjs(formData?.promo_expire_time).format(
                        "DD/MM/YYYY",
                      )}`}
                    </Typography>

                    <Typography variant="body1">
                      <span>ปริมาณของส่วนลด : </span>
                      {!!formData?.amount
                        ? convertIntToCurrency(formData?.amount, false)
                        : `ไม่จำกัด`}
                    </Typography>
                  </FieldValue>
                </Grid>

                <Grid item xs={12}>
                  <FormControl sx={{ width: "100%" }}>
                    <FormLabel
                      id="responsible-by-radio-group-label"
                      sx={{
                        fontSize: font.size.md,
                        color: "var(--secondary-color)",
                        mb: 2,
                        span: {
                          color: "var(--primary-color)",
                        },
                        "&.Mui-focused": {
                          color: "var(--secondary-color)",
                        },
                      }}
                    >
                      ผู้รับผิดชอบค่าใช้จ่ายของส่วนลด
                    </FormLabel>

                    <FieldValue>
                      <Typography variant="body1">
                        <span>XSURFACE : </span>
                        {formData?.promo_responsible?.promo_responsible_by ===
                        "xsurface"
                          ? `100%`
                          : !!formData?.promo_responsible
                                ?.promo_xsurface_percent
                            ? `${formData?.promo_responsible?.promo_xsurface_percent}%`
                            : `-`}
                      </Typography>

                      <Typography variant="body1">
                        <span>ร้านค้า : </span>
                        {formData?.promo_responsible?.promo_responsible_by ===
                        "seller"
                          ? `100%`
                          : !!formData?.promo_responsible?.promo_seller_percent
                            ? `${formData?.promo_responsible?.promo_seller_percent}%`
                            : `-`}
                      </Typography>
                    </FieldValue>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <SpanLabel>รายละเอียดการใช้ส่วนลด</SpanLabel>

                  <FieldValue>
                    <Typography variant="body1">
                      <span>รายละเอียดที่แสดงบนหน้าการ์ดส่วนลด : </span>
                      {formData?.promo_title}
                    </Typography>
                  </FieldValue>
                </Grid>

                <Grid item xs={12}>
                  <FieldValue>
                    <Typography variant="body2">รายละเอียดเพิ่มเติม</Typography>

                    <PreviewDesc
                      dangerouslySetInnerHTML={{
                        __html: formData?.promo_description,
                      }}
                    />
                  </FieldValue>
                </Grid>
              </Grid>
            </LabelSection>
          </BoxWrapper>

          <BoxWrapper>
            <LabelSection
              title="เลือกสินค้าสำหรับการใช้ส่วนลด"
              style={{ margin: 0 }}
              headerStyle={{ marginBottom: 16 }}
              titleStyle={{ fontSize: font.size.lg }}
            >
              {formData?.product_choice_promocodes?.length > 0 && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          mr: 1,
                          color: DarkGrey,
                        }}
                        component="span"
                      >
                        สินค้าที่เลือก :
                      </Typography>
                      <Typography
                        sx={{
                          color: Primary,
                        }}
                        component="span"
                      >
                        {formData?.product_choice_promocodes?.length}
                      </Typography>
                    </Box>
                  </Box>

                  <WrapperDataGrid>
                    <DataGrid
                      rows={formData?.product_choice_promocodes}
                      getRowId={(row) => row._id}
                      getRowHeight={() => "auto"}
                      columns={columns}
                      pageSize={10}
                      pagination
                      components={{
                        NoRowsOverlay,
                        NoResultsOverlay,
                      }}
                      componentsProps={{
                        basePopper: {},
                      }}
                      hideFooterSelectedRowCount
                      disableSelectionOnClick
                      disableColumnMenu
                    />
                  </WrapperDataGrid>
                </>
              )}
            </LabelSection>
          </BoxWrapper>

          {!!formData?.note && (
            <BoxWrapper>
              <LabelSection
                title="บันทึก"
                style={{ margin: 0 }}
                headerStyle={{ marginBottom: 24 }}
                titleStyle={{ fontSize: font.size.lg }}
              >
                <FieldValue>
                  <Typography variant="body1">{formData?.note}</Typography>
                </FieldValue>
              </LabelSection>
            </BoxWrapper>
          )}

          {checkAllowPermission("promo_codes.can_edit") && (
            <ButtonGroup
              flexDirection={"row"}
              justifyContent={"flex-end"}
              alignItems={"center"}
              columnGap={2}
            >
              <Button
                variant="outlined"
                size="large"
                // onClick={() => window.history.back()}
                onClick={() => navigate(`/promo-code/list`)}
                sx={{
                  width: "100%",
                  maxWidth: 140,
                  borderRadius: 4,
                }}
              >
                ย้อนกลับ
              </Button>

              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(`/promo-code/edit/${formData?._id}`)}
                sx={{ width: "100%", maxWidth: 140, borderRadius: 4 }}
              >
                แก้ไข
              </Button>
            </ButtonGroup>
          )}
        </Container>
      </Main>

      {isLoading && <Loading />}
    </>
  );
};

export default PreviewPromoCode;
